import React, { useEffect, useState } from 'react';
import { TextField } from '@fluentui/react';
import { normalizeEmailRecipients } from '../../../components/shared/common/util';

/**
 * A TextField whose typing state is kept local to this component so that
 * keystrokes do not re-render the large parent declarative form (which is
 * rebuilt on every state change). The parent-provided value is used to seed
 * and externally sync the field, while edits are propagated back to the parent
 * on blur (via the passed onBlur handler). This removes the typing lag that
 * occurred while a value change forced the whole Deal Information form to
 * re-render on every keystroke.
 *
 * On blur the displayed value is normalized here as well (whitespace removed,
 * semicolon separators tidied). This guarantees the field always trims on every
 * blur, even when the normalized result equals the value already committed to
 * the parent (in which case the parent `value` prop would not change and the
 * sync effect below would not fire).
 */
const DealRecipientsTextField: React.FC<any> = (props) => {
    const { value, onChange, onBlur, ...rest } = props;
    const [localValue, setLocalValue] = useState<string>(value || '');

    useEffect(() => {
        setLocalValue(value || '');
    }, [value]);

    const handleChange = (
        _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string
    ): void => {
        setLocalValue(newValue ?? '');
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        // Trim/normalize the visible value on every blur so spaces are always removed.
        setLocalValue(normalizeEmailRecipients(localValue));
        // Let the parent run its own validation/persistence off the raw field value.
        if (onBlur) {
            onBlur(event);
        }
    };

    return (
        <TextField
            {...rest}
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
        />
    );
};

export default DealRecipientsTextField;
